import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, HeartOutlined, SafetyOutlined, BulbOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import calmiImage from '../../../assets/images/Calmi.png';

const { Title, Text, Paragraph } = Typography;

const LunaLion = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('luna-lion', 'Luna the Lion', 499);
  }, []);

  const product = {
    id: 'luna-lion',
    name: 'Luna the Lion',
    price: 499,
    image: calmiImage,
    collection: 'Animal Friends',
    subtitle: 'Brave and bold companion',
    description: 'Golden-maned lion with confident posture and warm amber fur. Luna\'s strong stance and noble expression inspire courage and confidence.',
    fullDescription: 'Luna the Lion is designed for children who need to find their inner strength and courage. With her golden mane flowing proudly and warm amber fur, Luna embodies bravery and confidence. Her AI conversations focus on building self-worth, facing fears, and developing the courage to tackle life\'s challenges. Luna is perfect for children starting new schools, dealing with social anxiety, or simply needing a reminder of their own inner power.',
    specs: { 
      weight: '2.5 lbs', 
      dimensions: '12" x 8" x 6"', 
      color: 'Golden Amber', 
      texture: 'Maned Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Confidence-building AI conversations focused on bravery',
      'Flowing golden mane and noble lion design',
      'Warm amber coloring that radiates strength',
      'Conversations about facing fears and challenges',
      'Empowerment stories and courage-building activities',
      'Perfect for building social confidence'
    ],
    idealFor: [
      'Children facing new challenges or transitions',
      'Building self-confidence and self-worth',
      'Overcoming fears and anxieties',
      'Starting new schools or activities',
      'Dealing with social situations',
      'Children who need empowerment and encouragement'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Family
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🦁</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#fff8dc', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '3px solid #ffa500'
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  filter: 'sepia(30%) saturate(150%) hue-rotate(15deg)'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="orange">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#fff8dc' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications & Features */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            {Object.entries(product.specs).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Text strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</Text><br/>
                <Text>{value}</Text>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose Luna the Lion?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#ffa500', marginBottom: '16px' }} />
              <Title level={4}>Courage Builder</Title>
              <Text>
                Specifically designed to help children find their inner strength and face challenges with confidence.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#ff8c00', marginBottom: '16px' }} />
              <Title level={4}>Empowering Design</Title>
              <Text>
                The noble lion design and golden colors naturally inspire feelings of strength and bravery.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#ffd700', marginBottom: '16px' }} />
              <Title level={4}>Confidence Focus</Title>
              <Text>
                AI conversations specifically crafted to build self-worth and social confidence.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default LunaLion;